import type { ReactElement } from "react";

import type { OnboardingStepName } from "@/constants/Onboarding";
import { BaseOnboardingCard } from "@/ui/components/onboarding/v2/cards/BaseOnboardingCard";
import { getOnboardingStepContent } from "@/ui/components/onboarding/v2/content/OnboardingStepContent";
import { FAIconName, Icon, Text } from "@/ui/core";
import { Box, HStack } from "@/ui/layout";
import {
  getOnboardingIllustrationIcon,
  OnboardingIllustrationSize,
} from "@/ui/theme/icons/illustrations/onboarding";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

type OnboardingCollapsedStepCardState = "complete" | "pending";

interface OnboardingCollapsedStepCardProps extends ViewWithTestID {
  patientName?: string;
  state: OnboardingCollapsedStepCardState;
  stepName: OnboardingStepName;
}

const OnboardingCollapsedStepCard = ({
  patientName,
  state,
  stepName,
  testID,
}: OnboardingCollapsedStepCardProps): ReactElement => {
  const { iconName, title } = getOnboardingStepContent({
    patientName,
    stepName,
  });
  const isComplete = state === "complete";

  return (
    <BaseOnboardingCard isBorderDashed={!isComplete} testID={testID}>
      <HStack alignItems="center" gap={10}>
        {getOnboardingIllustrationIcon({
          iconName,
          size: OnboardingIllustrationSize.Small,
        })}
        <Box flex={1}>
          <Text
            color={ColorToken.NeutralSecondary}
            testID={joinTestIDs(testID, "title")}
            variant="captionMedium"
          >
            {title}
          </Text>
        </Box>
        {isComplete && (
          <Icon
            color={ColorToken.Primary100}
            name={FAIconName.Check}
            size="sm"
            testID={joinTestIDs(testID, "check")}
          />
        )}
      </HStack>
    </BaseOnboardingCard>
  );
};

export type {
  OnboardingCollapsedStepCardProps,
  OnboardingCollapsedStepCardState,
};
export { OnboardingCollapsedStepCard };
